import { useState, useRef, useCallback, useEffect } from 'react';

export const useWebRTC = ({ sendMessage, onFileReceived }) => {
  const [p2pFiles, setP2pFiles] = useState([]);
  const [p2pProgress, setP2pProgress] = useState({});
  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const incomingRef = useRef(null);
  const remotePeerIdRef = useRef(null);
  const pendingIceCandidatesRef = useRef([]);
  const lastReceivedChunkRef = useRef({});

  const iceServers = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
      {
        urls: 'turn:openrelay.metered.ca:80',
        username: 'openrelayproject',
        credential: 'openrelayproject',
      },
    ],
    sdpSemantics: 'unified-plan',
    iceCandidatePoolSize: 4,
  };

  const flushPendingIceCandidates = useCallback(async (pc) => {
    const pending = pendingIceCandidatesRef.current;
    pendingIceCandidatesRef.current = [];
    for (const candidate of pending) {
      try {
        await pc.addIceCandidate(candidate);
      } catch (e) {
        console.error('WebRTC: Pending ICE candidate error:', e);
      }
    }
  }, []);

  const bindIceCandidateHandler = useCallback((pc, peerId) => {
    pc.onicecandidate = (event) => {
      if (event.candidate && peerId) {
        sendMessage({
          type: 'webrtc_ice_candidate',
          to: peerId,
          candidate: event.candidate.candidate,
          sdpMid: event.candidate.sdpMid,
          sdpMLineIndex: event.candidate.sdpMLineIndex,
        });
      }
    };
  }, [sendMessage]);

  const ensurePC = useCallback(() => {
    if (pcRef.current && pcRef.current.connectionState !== 'closed') {
      return pcRef.current;
    }
    if (pcRef.current) {
      pcRef.current.close();
    }

    pendingIceCandidatesRef.current = [];
    const pc = new RTCPeerConnection(iceServers);

    pc.ondatachannel = (event) => {
      const dc = event.channel;
      dc.binaryType = 'arraybuffer';
      dc.onmessage = (msgEvent) => {
        if (typeof msgEvent.data === 'string') {
          try {
            const msg = JSON.parse(msgEvent.data);
            const incoming = incomingRef.current;
            if (msg.t === 'start') {
              incomingRef.current = {
                fileId: msg.id,
                name: msg.n,
                size: msg.s,
                mimeType: msg.m || '',
                chunks: [],
                received: 0,
              };
              setP2pProgress(prev => ({
                ...prev,
                [msg.id]: 0,
              }));
            } else if (msg.t === 'end') {
              if (incoming && incoming.fileId === msg.id) {
                const blob = new Blob(incoming.chunks, { type: incoming.mimeType });
                onFileReceived?.(incoming.name, blob, incoming.fileId);
                setP2pProgress(prev => {
                  const next = { ...prev };
                  delete next[msg.id];
                  return next;
                });
                incomingRef.current = null;
              }
            }
          } catch (e) {
            console.error('WebRTC: Control message parse error:', e);
          }
        } else {
          const incoming = incomingRef.current;
          if (incoming) {
            incoming.chunks.push(msgEvent.data);
            incoming.received += msgEvent.data.byteLength;
            // Track chunk count for resume capability
            lastReceivedChunkRef.current[incoming.fileId] = incoming.chunks.length;
            if (incoming.size > 0) {
              setP2pProgress(prev => ({
                ...prev,
                [incoming.fileId]: Math.round((incoming.received / incoming.size) * 100),
              }));
            }
          }
        }
      };

      dc.onopen = () => {
        console.log('WebRTC: Data channel open');
      };

      dc.onclose = () => {
        console.log('WebRTC: Data channel closed');
      };

      dcRef.current = dc;
    };

    pc.oniceconnectionstatechange = () => {
      console.log('WebRTC: ICE connection state:', pc.iceConnectionState);
    };

    pcRef.current = pc;
    return pc;
  }, [onFileReceived]);

  const handleOffer = useCallback(async (event) => {
    try {
      const peerId = event.from;
      if (!peerId) return;

      remotePeerIdRef.current = peerId;
      const pc = ensurePC();
      bindIceCandidateHandler(pc, peerId);

      await pc.setRemoteDescription(new RTCSessionDescription({
        type: event.sdpType,
        sdp: event.sdp,
      }));
      await flushPendingIceCandidates(pc);

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      sendMessage({
        type: 'webrtc_answer',
        to: peerId,
        sdp: answer.sdp,
        sdpType: answer.type,
      });
      sendMessage({ type: 'p2p_hello', role: 'receiver' });
    } catch (e) {
      console.error('WebRTC: Handle offer error:', e);
    }
  }, [ensurePC, bindIceCandidateHandler, flushPendingIceCandidates, sendMessage]);

  const handleIceCandidate = useCallback(async (event) => {
    try {
      const pc = pcRef.current;
      const candidate = new RTCIceCandidate({
        candidate: event.candidate,
        sdpMid: event.sdpMid,
        sdpMLineIndex: event.sdpMLineIndex,
      });

      if (!pc || !pc.remoteDescription) {
        pendingIceCandidatesRef.current.push(candidate);
        return;
      }

      await pc.addIceCandidate(candidate);
    } catch (e) {
      console.error('WebRTC: ICE candidate error:', e);
    }
  }, []);

  const handleAnnounceFile = useCallback((event) => {
    const fileId = event.fileId;
    const fileName = event.fileName;
    const fileSize = event.fileSize;
    const fileType = event.fileType;
    if (fileId && fileName) {
      setP2pFiles(prev => {
        if (prev.some(f => f.id === fileId)) return prev;
        return [...prev, { id: fileId, name: fileName, size: fileSize, type: fileType }];
      });
    }
  }, []);

  const cleanup = useCallback(() => {
    if (dcRef.current) {
      dcRef.current.close();
      dcRef.current = null;
    }
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    incomingRef.current = null;
    remotePeerIdRef.current = null;
    pendingIceCandidatesRef.current = [];
  }, []);

  useEffect(() => {
    return () => cleanup();
  }, [cleanup]);

  return {
    p2pFiles,
    p2pProgress,
    handleOffer,
    handleIceCandidate,
    handleAnnounceFile,
    ensurePC,
    cleanup,
    lastReceivedChunkRef,
  };
};
